// Este utilitário provê um mecanismo simples de cache para o accessToken do usuário
// APENAS para uso no lado do cliente (browser).

import { getSession } from 'next-auth/react';

let cachedToken: string | null = null; // último token obtido
let lastFetch = 0; // timestamp da última obtenção
const CACHE_TTL = 5_000; // 5 segundos
let fetchingPromise: Promise<string | null> | null = null; // lock p/ evitar concorrência

async function fetchAccessToken(): Promise<string | null> {
  const session = await getSession();
  // Se o accessToken estiver expirado, getSession já tentará renová-lo.
  if ((session as any)?.error === 'RefreshAccessTokenError') {
    return null;
  }
  return session?.user?.accessToken ?? null;
}

// Função pública utilizada pelos interceptors e outros helpers no cliente
export async function getAccessToken(): Promise<string | null> {
  const now = Date.now();
  // Retorna imediatamente se o token em cache ainda é válido
  if (cachedToken && now - lastFetch < CACHE_TTL) return cachedToken;
  // Evita múltiplos fetches simultâneos
  if (fetchingPromise) return fetchingPromise;

  fetchingPromise = (async () => {
    try {
      const token = await fetchAccessToken();
      cachedToken = token;
      lastFetch = Date.now();
      return token;
    } finally {
      // Libera o lock independente de sucesso/erro
      fetchingPromise = null;
    }
  })();

  return fetchingPromise;
}

// Interceptor que injeta o header Authorization em todas as requisições Axios no cliente
export async function tokenInterceptor(request: any) {
  try {
    const token = await getAccessToken();
    if (token) {
      request.headers = {
        ...request.headers,
        Authorization: `Bearer ${token}`,
      };
    }
  } catch (_) {
    // Em caso de erro, não modificamos a requisição
  }
  return request;
}

// Função para invalidar o cache explicitamente (ex.: após signOut)
export function clearTokenCache() {
  cachedToken = null;
  lastFetch = 0;
}
